import ReactDOM from 'react-dom';
import bbox from '@turf/bbox';
import center from '@turf/center';
import lineChunk from '@turf/line-chunk';
import { lineString } from '@turf/helpers';
import isEmpty from 'lodash/isEmpty';
import mapboxgl from 'mapbox-gl';
import mapStyles from './map.styles.js';

/**
  Draw an image on a mapbox instance
  @function drawImage
  @param {Map} map Mapbox instance
  @param {File} file File to use as the images source
  @param {Object} nwCorner NorthWest coordinates in the form { lat, lng }
  @param {Object} seCorner SouthEast coordinates in the form { lat, lng }
  @param {String} id ID to use for the map layer
  @param {String} afterId ID to use as referente to insert the new layer
*/
export const drawImage = (map, file, nwCorner, seCorner, id, afterId) => {
  if (!map.getLayer(`${id}-overlay-airfinder`)) {
    map.addSource(`${id}-airfinder`, {
      type: 'image',
      url: file,
      coordinates: [
        [nwCorner.lng, nwCorner.lat],
        [seCorner.lng, nwCorner.lat],
        [seCorner.lng, seCorner.lat],
        [nwCorner.lng, seCorner.lat]
      ]
    });
    map.addLayer(
      {
        id: `${id}-overlay-airfinder`,
        source: `${id}-airfinder`,
        type: 'raster'
      },
      afterId
    );
  }
};

/**
  Draw a polygon on a mapbox instance
  @function drawPolygon
  @param {Map} map Mapbox instance
  @param {List} polygon List of coordinates in the form [[lng, lat]]
  @param {String} id ID to use for the map layer
  @param {String} isArea If the polyon represents an area
*/
export const drawPolygon = (map, polygon, id, isArea) => {
  if (!map.getLayer(`${id}-fill-airfinder`)) {
    const locationsLayer = map.getLayer('tags-points-airfinder') ? 'tags-points-airfinder' : undefined ;
    map.addLayer({
      id: `${id}-fill-airfinder`,
      type: 'fill',
      source: {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [polygon]
          }
        }
      },
      layout: {},
      paint: isArea ? mapStyles.areaFill : mapStyles.polygonFill
    });
    map.addLayer({
      id: `${id}-border-airfinder`,
      type: 'line',
      source: {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [polygon]
          }
        }
      },
      layout: {},
      paint: isArea ? mapStyles.areaBorder : mapStyles.polygonBorder
    }, locationsLayer);
  }
};

/**
  Draw a line on a mapbox instance
  @function drawLine
  @param {Map} map Mapbox instance
  @param {List} coordinates List of coordinates in the form [[lng, lat]]
  @param {String} id ID to use for the map layer
  @param {String} afterId ID to use as referente to insert the new layer
*/
export const drawLine = (map, coordinates, id, afterId) => {
  if (map.getLayer(`${id}-line-airfinder`)) {
    let source = map.getSource(`${id}-line-airfinder`);
    source.setData({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates
      }
    });
  } else {
    map.addLayer(
      {
        id: `${id}-line-airfinder`,
        type: 'line',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates
            }
          }
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: mapStyles.line
      },
      afterId
    );
  }
};

/**
  Draw or Modify a point on a mapbox instance
  @function drawPoint
  @param {Map} map Mapbox instance
  @param {List} point Coordinates in the form [lng, lat]
  @param {String} id ID to use for the map layer
  @param {boolean} hasCounts hasCounts type
  @param {String} text Text to print inside the point
  @param {String} type type of point
  @returns {String} ID of the layer created/modified
*/
export const drawPoint = (map, point, id, hasCounts, text = '', type = 'circle') => {
  if (map.getLayer(`${id}-point-airfinder`)) {
    if(hasCounts){
      let count = map.getSource(`${id}-point-count-airfinder`);
        count.setData({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: point
          },
          properties: {
            title: text
          }
      });
    }
    
  } else {
    let opts = {
      id: `${id}-point-airfinder`,
      type: 'circle',
      source: {
        type: 'geojson',
        data: {
          type: 'Point',
          coordinates: point
        }
      }      
    };

    if(type === 'circle'){
      opts.paint = mapStyles.circle;
    }else{
      opts.paint = {
          'circle-radius': 
          {
            stops: [[1, 2], [8,3], [10,4]]
          },
          'circle-color': mapStyles[type]['circle-color'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#333333'
        }
    }

    map.addLayer(opts);
    if(hasCounts){
      map.addLayer({
        id: `${id}-point-count-airfinder`,
        type: 'symbol',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: point
            },
            properties: {
              title: text
            }
          }
        },
        layout: mapStyles.textLayout,
        paint: mapStyles.text
      });
    }
  }
  return `${id}-point-airfinder`;
};

/**
  Draw or Modify collection of points on a mapbox instance
  @function drawPoints
  @param {Map} map Mapbox instance
  @param {List} point Coordinates in the form [[lng1, lat1],[lng2, lat2]]
  @param {String} id ID to use for the map layer
  @param {String} text Text to print inside the point
*/
export const drawPoints = (map, points, id, type = 'circle') => {
  const pointsLayerId = `${id}-points-airfinder`;
  const pointsCountsLayerId = `${id}-points-counts-airfinder`;
  if (map.getLayer(pointsLayerId)) {
    let countsLayer = map.getSource(pointsCountsLayerId);
    countsLayer.setData({
      type: 'FeatureCollection',
      features: points
    });
    let pointsLayer = map.getSource(pointsLayerId);
    pointsLayer.setData({
      type: 'FeatureCollection',
      features: points
    });
  }
  else {
    let opts = {
      id: pointsLayerId,
      type: 'circle',
      source: {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: points
        }
      }
    };
    if (type === 'circle') {
      opts.paint = mapStyles.circle;
    } else {
      opts.paint = {
        'circle-radius':
        {
          stops: [[1, 2], [8, 3], [10, 4]]
        },
        'circle-color': mapStyles[type]['circle-color'],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#333333'
      }
    }
    map.addLayer(opts);

    map.addLayer({
      id: pointsCountsLayerId,
      type: 'symbol',
      source: {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: points
        }
      },
      layout: mapStyles.textLayout,
      paint: mapStyles.text
    });
  }
  return pointsLayerId;
};

/**
  Draw or Modify a marker on a mapbox instance
  @function drawMarker
  @param {Map} map Mapbox instance
  @param {List} point coordinates in the form [lng, lat]
  @param {String} id ID to use for the map layer]
  @param {String} type Marker type
  @param {boolean} type hasCounts type
  @returns {String} ID of the layer created/modified
*/
export const drawMarker = (map, point, id, type, hasCounts) => {
  return drawPoint(map, point, id, hasCounts, '', type);
};

/**
  Draw a popup on a mapbox instance
  @function drawPopup
  @param {Map} map Mapbox instance
  @param {List} coordinates coordinates in the form [[lng, lat]]
  @param {String} description Text in the popup
  @return {Object} Popup instance
*/
export const drawPopup = (map, coordinates, description) => {
  let div = document.createElement('div');
  return new mapboxgl.Popup()
    .setLngLat(coordinates)
    .setDOMContent(ReactDOM.render(description, div))
    .addTo(map);
};

/** Remove a point from a mapbox instance
    @function removePoint
    @param {Map} map Mapbox instance
    @param {String} id ID of the point to remove
*/
export const removePoint = (map, id) => {
  if (map.getLayer(`${id}-point-airfinder`)) {
    map.removeLayer(`${id}-point-airfinder`);
    map.removeLayer(`${id}-point-count-airfinder`);
    map.removeSource(`${id}-point-airfinder`);
    map.removeSource(`${id}-point-count-airfinder`);
  }
};

/**
  Fit a map instance to a set of coordinates
  @function fitPolygon
  @param {Map} map Mapbox instance
  @param {List} polygon List of coordinates of the form [[lng,lat]]
*/
export const fitPolygon = (map, polygon) => {
  map.fitBounds(
    bbox({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [polygon]
      }
    }),
    { linear: true, padding: { top: 25, bottom: 25, right: 25, left: 25 } }
  );
};

/**
  Get the center of a set of coordinates
  @function getPolygonCenter
  @param {Map} map Mapbox instance
  @param {List} coordinates Coordinates in the form [lng, lat]
  @returns {List} center coordinates in the form [lng, lat]
*/
export const getPolygonCenter = polygon => {
  return center({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [polygon]
    }
  }).geometry.coordinates;
};

/**
  Clear custom layers from a map instance
  @function clearLayers
  @param {Map} map Mapbox instance
*/
export const clearLayers = map => {
  let { layers, sources } = map.getStyle();
  layers.forEach(layer => {
    if (layer.id.includes('-airfinder')) {
      map.removeLayer(layer.id);
    }
  });
  Object.keys(sources).forEach(source => {
    if (source.includes('-airfinder')) {
      map.removeSource(source);
    }
  });
};

export const hasLayers = map => {
  let { layers } = map.getStyle();
  let has = false
  layers.forEach(layer => {
    if (layer.id.includes('-fill-airfinder')) {
      has = true;
    }
  });
  return has;
};

export const hasArea = map => {
  let { layers } = map.getStyle();
  let has = false
  layers.forEach(layer => {
    if (layer.id.includes('area-polygon-fill-airfinder')) {
      has = true;
    }
  });
  return has;
};

//the code should always draw all zones, so just check 1
export const hasZones = (map, id) => {
  let { layers } = map.getStyle();
  let has = false
  layers.forEach(layer => {
    if (layer.id.includes(`${id}-fill-airfinder`)) {
      has = true;
    }
  });
  return has;
};

export const clearTags = map => {
  let { layers, sources } = map.getStyle();
  layers.forEach(layer => {
    if (layer.id.includes('-point-airfinder')) {
      map.removeLayer(layer.id);
    }
  });
  Object.keys(sources).forEach(source => {
    if (source.includes('-point-airfinder')) {
      map.removeSource(source);
    }
  });
};



export const clearLine = map =>{
  let { layers, sources } = map.getStyle();
  layers.forEach(layer => {
    if (layer.id.includes('-line-airfinder')) {
      map.removeLayer(layer.id);
    }
  });
  Object.keys(sources).forEach(source => {
    if (source.includes('-line-airfinder')) {
      map.removeSource(source);
    }
  });
}

/**
  Obtain the first custom layer from a map instance
  @function getFirstLayer
  @param {Map} map Mapbox instance
  @returns {String} ID of the first layer
*/
export const getFirstLayer = map => {
  try {
    const layers = map.getStyle().layers;
    for (var i = 0; i < layers.length; i++) {
      if (layers[i].id.includes('airfinder')) {
        return layers[i].id;
      }
    }
  } catch (e) {}
  return null;
};

/**
  Draw an animated line on a mapbox instance
  @function drawLine
  @param {Map} map Mapbox instance
  @param {List} coordinates coordinates in the form [lat, lng]
  @param {String} id ID to use for the map layer
*/
export const drawAnimatedLine = (map, coordinates, id) => {
  let progress = 0;
  let hasStarted = false;
  let speedFactor = 0.04; //km/ms
  let startTime = 0;
  let animation;
  let animateLine = timestamp => {
    if (!hasStarted) {
      startTime = performance.now();
      hasStarted = true;
    } else {
      progress = timestamp - startTime;
    }
    if (progress > 0 && map.isStyleLoaded()) {
      let newCoordinates = lineChunk(
        lineString(coordinates),
        speedFactor * progress
      ).features[0].geometry.coordinates;
      drawLine(map, newCoordinates, id, getFirstLayer(map));
      let newLastCoordinate = newCoordinates[newCoordinates.length - 1];
      let lastCoordinate = coordinates[newCoordinates.length - 1];
      if (
        newLastCoordinate[0] === lastCoordinate[0] &&
        newLastCoordinate[1] === lastCoordinate[1]
      ) {
        return;
      }
    }

    animation = requestAnimationFrame(animateLine);
  };

  return {
    start: () => {
      drawLine(map, [coordinates[0]], id, getFirstLayer(map));
      animateLine();
    },
    stop: () => cancelAnimationFrame(animation)
  };
};

/**
  Draw an area on a mapbox instance
  @function drawArea
  @param {Map} map Mapbox instance
  @param {Object} area The area object instace with the area data
  @param {Boolean} fitArea If the map should be fitted to contain this area
*/
export const drawArea = (map, area, fitArea) => {
  if (!isEmpty(area.indoorMapping)) {
    let { nwCorner, seCorner } = area.indoorMapping;
    if (fitArea) {
      map.setCenter([0, 0]);
    }
    let file = area.file && !!area.file.length && area.file[0].preview;
    if (file) {
      drawImage(map, file, nwCorner, seCorner, 'area', getFirstLayer(map));
    }
  }
  if (area.polygon) {
    drawPolygon(map, area.polygon, 'area-polygon', true);
    if (fitArea) {
      fitPolygon(map, area.polygon);
    }
  }
};

/**
  Draw all zones on a mapbox instance
  @function drawZones
  @param {Map} map Mapbox instance
  @param {List} zones A list of all zones to draw with their data
  @param {Map} locations Location data grouped by zone
  @param {Map} tags Tags data grouped by location
  @param {Function} onLocationClick Function to call when a click is performed
*/
export const drawZones = (map, zones, locations, tags, onLocationClick) => {
  if (zones) {
    zones.forEach(zone => {
      if (zone.polygon) {
        drawPolygon(map, zone.polygon, zone.id);
      }
    });
  }
};


/**
  Draw all locations on a mapbox instance
  @function drawLocations
  @param {Map} map Mapbox instance
  @param {List} zones A list of all zones to draw with their data
  @param {Map} locations Location data grouped by zone
  @param {Map} tags Tags data grouped by location
  @param {Function} onLocationClick Function to call when a click is performed
*/
let onClickHandler;
let onMouseEnterHandler;
let onMouseLeaveHandler;
export const drawLocations = (map, zones, locations, tags, onLocationClick) => {
  let ll = []
  if(locations){
    for (let k in locations) {
      ll = ll.concat(locations[k])
    }
  }
  const collection = [];
  ll.forEach(location => {
    if (location.point && location.id in tags && tags[location.id].length) {
      collection.push({
        type: "Feature",
        geometry: {
          type: 'Point',
          coordinates: location.point
        },
        properties: {
          title: tags[location.id].length,
          locationId: location.id
        }
      })
    }
  })

  const pointLayerId = drawPoints(
    map,
    collection,
    'tags'
  );
  if(onClickHandler){
    map.off('click', pointLayerId, onClickHandler);
    map.off('mouseenter', pointLayerId, onMouseEnterHandler);
    map.off('mouseleave', pointLayerId, onMouseLeaveHandler);
  }

  onClickHandler = (e) => {
    const clickedLocation = ll.find(location => location.id === e.features[0].properties.locationId);
    onLocationClick && clickedLocation && onLocationClick(clickedLocation);
  }

  onMouseEnterHandler = () => (map.getCanvas().style.cursor = 'pointer');
  onMouseLeaveHandler = () => (map.getCanvas().style.cursor = '');

  map.on(
    'click',
    pointLayerId,
    onClickHandler
  );
  map.on(
    'mouseenter',
    pointLayerId,
    onMouseEnterHandler
  );
  map.on(
    'mouseleave',
    pointLayerId,
    onMouseLeaveHandler
  );
};

/**
  Draw geoJSON on a mapbox instance
  @function drawGeoJson
  @param {Map} map Mapbox instance
  @param {List} geoJson A list of all points to draw
*/
export const drawGeoJson = (map, geoJson) => {
  drawPoints(
    map,
    geoJson,
    'tags'
  );
}

/**
  Draw all super tags on a mapbox instance
  @function drawSuperTags
  @param {Map} map Mapbox instance
  @param {List} superTags A list of all super tags to draw with their data
  @param {Function} onSuperTagClick Function to call when a click is performed
*/
export const drawSuperTags = (map, superTags, onSuperTagClick) => {
  superTags.forEach(tag => {
    let markerId = drawMarker(
      map,
      [tag.longitude, tag.latitude],
      tag.id,
      tag.source,
      false
    );
    map.on('click', markerId, () => onSuperTagClick(tag.id));
    map.on(
      'mouseenter',
      markerId,
      () => (map.getCanvas().style.cursor = 'pointer')
    );
    map.on('mouseleave', markerId, () => (map.getCanvas().style.cursor = ''));
  });
};

/**
  Draw all super tag history on a mapbox instance
  @function drawHistory
  @param {Map} map Mapbox instance
  @param {List} history A list of all the history data
  @param {Function} onAnimationLoad Function to call when the animation is loaded
*/
export const drawHistory = (
    map,
    history,
    onAnimationLoad
  ) => {
  let l = map.getLayer('tag-history');
  if(l){
    map.removeLayer(l.id);
  }
  if (history.features.length) {
    let s = map.getSource('tag-history-data');
    if(s){
      s.setData(history);
    }else{
      map.addSource('tag-history-data',{
        type: 'geojson',
        data: history
      })  
    }    
    map.addLayer({
      id: 'tag-history',
      source: 'tag-history-data',
      type:'circle',
      paint: {
        'circle-radius': 
        {
          stops: [[1, 4], [8,6], [10,8]]
        },
        'circle-blur': 0.5,
        'circle-color': mapStyles['gps']['circle-color'],
        'circle-stroke-width': 6,
        'circle-stroke-opacity': 0.65,
        'circle-stroke-color':  mapStyles['gps']['circle-color']
      }
    })
    let coordinates = history.features.map(
      tag =>{
        return tag.geometry.coordinates;
      }
        
    );
    fitPolygon(map, coordinates);
  }
};


// WEBPACK FOOTER //
// ./src/utils/mapgl/index.js